/**
 * CALL-CENTER-CRM-WORKSPACE-V1 -- pure view logic for the one-queue
 * workspace (agent "My Work" lens + manager "Team operations" lens).
 *
 * PURE ONLY: access decision, the URL-stable filter codec, the client-side
 * filter partitions over the ONE server-composed items array, and the small
 * render vocabulary. No fetch, no React -- react-dom/server tests pin every
 * branch. The server owns composition, dedup, and the due-ness order; the
 * client owns DISPLAY filtering only (ONE-QUEUE LAW: the tenant's buckets are
 * filters over one queue, never tabs -- the filters here can only NARROW).
 *
 * URL STATE (the lead-list-url precedent): versioned, allowlisted, PHI-free.
 * Keys: v (version), reason (csv of #330 tokens), due (urgency tier),
 * agent (numeric staff id -- manager drill; NEVER a name in the URL).
 * Defaults serialize as ABSENT so the bare URL stays canonical; unknown
 * keys/tokens are DROPPED on parse (fail-closed, refresh-safe).
 */

import type {
  CallcenterWorkspaceItemView,
  CallcenterWorkspaceView,
  WorkspaceWorkReason,
  WorkspaceWorkloadRowView,
} from '@/dashboard/api';
import { buildQuickViewFromTileItem, type LeadQuickViewModel } from '@/dashboard/leads/lead-quickview-view';
import type { DashboardMe } from '@/dashboard/types';
import { LEAD_LIST_URL_VERSION } from '@/dashboard/leads/lead-list-url';
import { formatIstDateTime } from '@/lib/datetime-ist';

export const CALLCENTER_WORKSPACE_URL_VERSION = '1';

/** The workspace route (ONE route, both lenses -- the server picks by role). */
export const CALLCENTER_WORKSPACE_PATH = '/dashboard/callcenter/workspace';

/** The canonical Lead Operations list route (the ONE canonical lead table) --
 *  the MANAGER DRILL-DOWN target (surface model: Team Operations drills into
 *  "Lead Operations filtered"; Zone Overview drills into "Lead Operations"). */
export const LEAD_OPERATIONS_PATH = '/dashboard/leads';

/**
 * FIX 4 (MANAGER DRILL-DOWN LAW, CALL-CENTER-SURFACE-AND-TRUTH-CORRECTIVE-V1):
 * the canonical Lead Operations drill PATH filtered to ONE agent (a numeric
 * staff id) or the unassigned pool -- a LINK into the ONE canonical lead list
 * (the lead-list-url `assignee` contract), never a duplicate table. PHI-free:
 * a bounded positive integer id or the 'unassigned' token. A non-integer /
 * non-positive id falls back to the unfiltered list (never an unparseable URL).
 */
export function leadOperationsDrillPath(target: number | 'unassigned'): string {
  const params = new URLSearchParams();
  params.set('v', LEAD_LIST_URL_VERSION);
  if (target === 'unassigned') {
    params.set('assignee', 'unassigned');
  } else if (Number.isSafeInteger(target) && target > 0) {
    params.set('assignee', String(target));
  }
  return `${LEAD_OPERATIONS_PATH}?${params.toString()}`;
}

// ---- Access decision (the per-surface guard pattern) -----------------------

export type CallCenterWorkspaceAccess =
  | { readonly kind: 'wait' }
  | { readonly kind: 'redirect'; readonly to: string }
  | { readonly kind: 'render' };

/**
 * Fail-closed access decision keyed on the SERVER-computed /me signal
 * (callCenterWorkspaceEnabled -- per-org gate x workspace audience). Loading
 * waits (the page never mounts, so no fetch fires); an error or missing /me
 * goes to login; a signed-in caller without the signal goes to their own
 * homePath (gate off = invisible; unauthorized role = invisible). Backend
 * route auth remains the security boundary either way.
 */
export function decideCallCenterWorkspaceAccess(args: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
}): CallCenterWorkspaceAccess {
  if (args.loading) return { kind: 'wait' };
  if (args.error !== null || args.me === null) {
    return { kind: 'redirect', to: '/login' };
  }
  if (args.me.callCenterWorkspaceEnabled !== true) {
    return { kind: 'redirect', to: args.me.homePath };
  }
  return { kind: 'render' };
}

// ---- URL-stable filter state ----------------------------------------------

/** The due-ness filter chips (server urgency tiers + 'all'). CALL-CENTRE-
 *  COMPLETION-001 (L1): 'upcoming' is the forward tier -- a promised callback
 *  whose time has not yet arrived but falls inside the forward window
 *  (lib/domain/lead/work-queue.ts CALLBACK_UPCOMING_WINDOW_MS). It carries no
 *  counted tile (dueHasCountedTile), so it renders as a chip beside 'all'. */
export const WORKSPACE_DUE_FILTERS = [
  'all',
  'breach',
  'due_now',
  'upcoming',
  'morning_priority',
] as const;

export type WorkspaceDueFilter = (typeof WORKSPACE_DUE_FILTERS)[number];

export interface CallCenterWorkspaceFilterState {
  /** Empty set = every reason (the whole queue). */
  readonly reasons: readonly WorkspaceWorkReason[];
  readonly due: WorkspaceDueFilter;
  /** Manager drill: the selected agent's staff id, or null = whole team. */
  readonly agentStaffId: number | null;
}

export const WORKSPACE_DEFAULT_FILTERS: CallCenterWorkspaceFilterState =
  Object.freeze({ reasons: [], due: 'all', agentStaffId: null });

const WORK_REASON_TOKENS: readonly WorkspaceWorkReason[] = Object.freeze([
  'new_lead',
  'call_now',
  'callback',
  'appointment_confirmation',
  'waiting_response',
  'no_show_recovery',
  'overdue_followup',
  'escalation',
  'routing_exception',
]);

function isWorkReasonToken(v: string): v is WorkspaceWorkReason {
  return (WORK_REASON_TOKENS as readonly string[]).includes(v);
}

function isDueFilter(v: string): v is WorkspaceDueFilter {
  return (WORKSPACE_DUE_FILTERS as readonly string[]).includes(v);
}

/** Parse the workspace URL params (unknown keys/tokens dropped; fail-closed). */
export function parseWorkspaceUrl(
  params: URLSearchParams,
): CallCenterWorkspaceFilterState {
  const reasonsRaw = params.get('reason') ?? '';
  const reasons = reasonsRaw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .filter(isWorkReasonToken);
  const dueRaw = params.get('due') ?? 'all';
  const due = isDueFilter(dueRaw) ? dueRaw : 'all';
  const agentRaw = params.get('agent');
  const agentNum = agentRaw === null ? NaN : Number(agentRaw);
  const agentStaffId =
    Number.isSafeInteger(agentNum) && agentNum > 0 ? agentNum : null;
  return { reasons: [...new Set(reasons)], due, agentStaffId };
}

/** Serialize filter state (defaults ABSENT; deterministic key order). */
export function serializeWorkspaceUrl(
  state: CallCenterWorkspaceFilterState,
): URLSearchParams {
  const params = new URLSearchParams();
  const isDefault =
    state.reasons.length === 0 &&
    state.due === 'all' &&
    state.agentStaffId === null;
  if (isDefault) return params;
  params.set('v', CALLCENTER_WORKSPACE_URL_VERSION);
  if (state.reasons.length > 0) params.set('reason', state.reasons.join(','));
  if (state.due !== 'all') params.set('due', state.due);
  if (state.agentStaffId !== null) {
    params.set('agent', String(state.agentStaffId));
  }
  return params;
}

/** Toggle one reason chip (multi-select filters, never tabs). */
export function toggleReason(
  state: CallCenterWorkspaceFilterState,
  reason: WorkspaceWorkReason,
): CallCenterWorkspaceFilterState {
  const has = state.reasons.includes(reason);
  return {
    ...state,
    reasons: has
      ? state.reasons.filter((r) => r !== reason)
      : [...state.reasons, reason],
  };
}

// ---- The client-side display partition (narrow-only) -----------------------

/**
 * Filter the server-ordered items. An agent filter matches by the owner's
 * staff id where the source wire carries one (station rows) and by the
 * selected agent's display label otherwise (the SLA/chase/intake wires carry
 * labels only -- an honest match, documented; a label collision shows BOTH
 * agents' rows, visible rather than hidden). Order is preserved (the server's
 * due-ness sort is the ONE order).
 */
export function filterWorkspaceItems(
  items: readonly CallcenterWorkspaceItemView[],
  state: CallCenterWorkspaceFilterState,
  agentLabelById: ReadonlyMap<number, string>,
  strictIdentity = false,
): CallcenterWorkspaceItemView[] {
  const reasonSet =
    state.reasons.length > 0
      ? new Set<WorkspaceWorkReason>(state.reasons)
      : null;
  return items.filter((item) => {
    if (reasonSet !== null && !reasonSet.has(item.workReason)) return false;
    if (state.due !== 'all' && item.urgency !== state.due) return false;
    if (state.agentStaffId !== null) {
      // Complete manager responses carry stable staff IDs on every lead.
      // Legacy label-only responses are not safe to use for agent filtering.
      const byId = item.owner.staffId === state.agentStaffId;
      const byLabel = !strictIdentity && item.owner.label !== null && item.owner.label === agentLabelById.get(state.agentStaffId);
      if (!byId && !byLabel) return false;
    }
    return true;
  });
}

// ---- Render vocabulary (labels only; tokens never rendered raw) ------------

/** Owner cell text per the OWNER pin: explicit on EVERY row, never blank. */
export function ownerCellLabel(
  owner: CallcenterWorkspaceItemView['owner'],
): string {
  switch (owner.kind) {
    case 'you':
      return 'Assigned to you';
    case 'staff':
      return owner.label ?? 'Owner not recorded';
    case 'exception':
      return 'Unassigned - needs owner';
    case 'unknown':
      return 'Ownership unavailable';
    default: {
      const _exhaustive: never = owner.kind;
      void _exhaustive;
      return 'Ownership unavailable';
    }
  }
}

/** The honest due-state label (urgency + the no-due state). */
export function dueStateLabel(item: CallcenterWorkspaceItemView): string {
  switch (item.urgency) {
    case 'breach':
      return `Overdue - ${formatAge(item.ageMinutes)}`;
    case 'due_now':
      return `Due now - waiting ${formatAge(item.ageMinutes)}`;
    case 'morning_priority':
      return 'Morning priority';
    case 'upcoming':
      // A promised, not-yet-due callback: the honest fact is WHEN it is due,
      // never an elapsed "waiting" duration (ageMinutes is null for this
      // tier by construction -- see upcomingWorkItemFromCallback).
      return item.slaAnchorIso === null
        ? 'Upcoming'
        : `Promised - ${formatIstDateTime(item.slaAnchorIso)}`;
    case 'none':
      return item.slaAnchorIso === null
        ? 'No due time'
        : `Waiting ${formatAge(item.ageMinutes)}`;
    default: {
      const _exhaustive: never = item.urgency;
      void _exhaustive;
      return 'No due time';
    }
  }
}

/** The due-filter chip labels (human language over the tier tokens). */
export const WORKSPACE_DUE_FILTER_LABELS: Readonly<
  Record<WorkspaceDueFilter, string>
> = Object.freeze({
  all: 'Everything',
  breach: 'Overdue',
  due_now: 'Due now',
  upcoming: 'Upcoming',
  morning_priority: 'Morning priority',
});

/** Compact whole-unit age label (the NoShowRecoveryPage / manager-glance
 *  idiom, so every desk reads the same way). Null -> '-'. */
export function formatAge(minutes: number | null): string {
  if (minutes === null) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Working-hours explainer for the morning-priority chip -- rendered from the
 *  org CONFIG the wire carries, never a hardcoded clock claim. */
export function morningPriorityExplainer(
  view: Pick<CallcenterWorkspaceView, 'workingHours'>,
): string {
  return `Arrived after hours - the response clock starts at ${view.workingHours.open}`;
}

/* ------------------------------------------------------------------------ *
 * COMMAND-CENTER-CRAFT-V2 -- BECAUSE-LINES ON THE TILES (founder review of
 * #680: "the tiles carry no because-lines -- 25 / OVERDUE has no age, no
 * oldest").
 *
 * KpiTile ALREADY HAS THE SLOT. Its own header states the intent -- "optional
 * plain sub line, severity tone ... color never the sole carrier: THE SUB
 * LINE NAMES THE STATE" -- and this screen never passed one, so colour WAS
 * the sole carrier of state on six tiles. The primitive is unchanged; the
 * screen starts using the slot it was given.
 *
 * NOT the `hint` prop: KpiTile renders `hint` as `title=`, a native tooltip.
 * That is invisible when scanning and unreachable on a touch screen -- the
 * same defect V1 fixed on the table row and left standing on the tiles.
 *
 * COMPOSED FROM LOADED DATA. The age comes from the items the page already
 * fetched; no new query, no read-model change.
 *
 * THE HONESTY FENCE: the strip COUNTS are the server's over the whole set,
 * while the ages here are derived from the LOADED items, which the server may
 * have capped (`cappedSources`). A capped feed makes "oldest" a lower bound,
 * so when anything is capped the age fact is OMITTED rather than stated as
 * fact. An absent sub-line is honest; a confidently wrong one is not.
 * ------------------------------------------------------------------------ */

/** The oldest loaded item matching `match`, as a because-line ("oldest 14d"),
 *  or null when nothing matches or the feed is capped. */
export function oldestAgeFact(
  view: Pick<CallcenterWorkspaceView, 'items' | 'cappedSources'>,
  match: (item: CallcenterWorkspaceItemView) => boolean,
): string | null {
  if (view.cappedSources.length > 0) return null;
  let oldest: number | null = null;
  for (const item of view.items) {
    if (!match(item)) continue;
    if (item.ageMinutes === null) continue;
    if (oldest === null || item.ageMinutes > oldest) oldest = item.ageMinutes;
  }
  return oldest === null ? null : `oldest ${formatAge(oldest)}`;
}

/** The because-line for each strip tile. `null` = no honest fact to add, and
 *  the tile renders exactly as it did before. */
export interface StripTileFacts {
  readonly newLeads: string | null;
  readonly dueNow: string | null;
  readonly overdue: string | null;
  readonly appointmentsAtRisk: string | null;
  readonly morningPriority: string | null;
  readonly exceptions: string | null;
}

export function composeStripTileFacts(
  view: Pick<CallcenterWorkspaceView, 'items' | 'cappedSources' | 'workingHours'>,
): StripTileFacts {
  return {
    // BECAUSE-LINES GO WHERE WORK IS STUCK, not on every tile. The founder's
    // finding named the overdue tile: "25 / OVERDUE has no age, no oldest".
    // The three tiles the screen already tones `breach` are exactly the ones
    // whose question is "how bad is it" -- an age answers that. New leads and
    // due-now are not late yet by definition, so an age there would be text
    // without a decision behind it, and morning-priority's reason already
    // rides the row where the operator is working.
    //
    // The tone system decides this, not a budget: the tiles that carry colour
    // carry the reason for it, which is the primitive's own rule -- "colour
    // never the sole carrier: the sub line names the state".
    newLeads: null,
    dueNow: null,
    overdue: oldestAgeFact(view, (i) => i.urgency === 'breach'),
    appointmentsAtRisk: oldestAgeFact(
      view,
      (i) => i.workReason === 'appointment_confirmation',
    ),
    morningPriority: null,
    exceptions: oldestAgeFact(view, (i) => i.workReason === 'routing_exception'),
  };
}

/* ------------------------------------------------------------------------ *
 * COMMAND-CENTER-CRAFT-V2 -- QUIET ZEROS (founder review of #680: "0 DUE NOW
 * occupies the same real estate as 25 OVERDUE").
 *
 * A zero tile costs a full card to say nothing is there, and its filter leads
 * to an empty queue -- a control nobody uses to do their job. So a zero
 * collapses into one quiet line naming what is clear, and the card is not
 * drawn. The FACT survives; the real estate does not.
 *
 * THE EXCEPTION THAT MATTERS: a tile that is the ACTIVE filter always keeps
 * its card even at zero. Without that, filtering to a bucket that empties
 * would make the control you are standing on vanish underneath you.
 * ------------------------------------------------------------------------ */

export function tileRendersAsCard(value: number, active: boolean): boolean {
  return value > 0 || active;
}

/** One strip tile, as the page is about to render it. */
export interface StripTileState {
  readonly label: string;
  readonly value: number;
  readonly active: boolean;
}

/** The labels of the tiles that collapse, in strip order -- the input to the
 *  quiet line. PURE, and computed BEFORE the JSX rather than accumulated as a
 *  side effect while children evaluate: render order is not a contract worth
 *  depending on, and a reader should be able to see the whole decision in one
 *  place. */
export function collapsedTileLabels(
  tiles: readonly StripTileState[],
): readonly string[] {
  return tiles
    .filter((tile) => !tileRendersAsCard(tile.value, tile.active))
    .map((tile) => tile.label);
}

/** The quiet all-clear sentence for the tiles that collapsed, or null when
 *  none did. Plural-safe and ordered as the strip orders them.
 *
 *  LABELS ARE VERBATIM, never lower-cased. The ONE-NUMBER LAW corrective
 *  requires every count to carry its scope-carrying label exactly -- and
 *  lower-casing turns "Overdue (SLA breaches)" into "overdue (sla breaches)",
 *  which drops the acronym that makes the scope readable. A collapsed tile
 *  keeps the same vocabulary as the card it replaced. */
export function quietZeroLine(labels: readonly string[]): string | null {
  if (labels.length === 0) return null;
  if (labels.length === 1) return `Nothing in ${labels[0]}.`;
  const head = labels.slice(0, -1).join(', ');
  return `Nothing in ${head} or ${labels[labels.length - 1]}.`;
}

/* ------------------------------------------------------------------------ *
 * COMMAND-CENTER-CRAFT-V1 -- THE DUPLICATE-FILTER RULE (founder amendment
 * 2026-09-01: the element count must go DOWN, not up).
 *
 * THIS SCREEN SHIPPED THE SAME FILTER TWICE. The KPI tiles are already
 * filters -- each one carries `active` and writes the filter on click -- AND
 * the chip row below repeated most of them as plain buttons. The tile version
 * is strictly richer: it carries the COUNT and the semantic TONE. The chip
 * version carries neither.
 *
 * So a filter that already has a counted tile does not also need an
 * uncounted chip. NO CAPABILITY IS REMOVED: every filter the chip row offered
 * is still one click away, on the tile that also tells you how big it is.
 * The chips that SURVIVE are exactly the ones with no tile -- the reasons the
 * strip does not count (callback, overdue follow-up, escalation, ...) and
 * "Everything", which is the clear-all and has no tile by nature.
 *
 * LENS-AWARE ON PURPOSE: the unassigned-exceptions tile is manager-only, so
 * on the AGENT lens that chip is NOT a duplicate and stays. A rule that
 * ignored the lens would delete an agent's only route to that filter.
 * ------------------------------------------------------------------------ */

/** Reasons the strip renders as a counted tile. `routing_exception` is
 *  manager-only (the tile is), hence the lens argument. */
export function reasonHasCountedTile(
  reason: WorkspaceWorkReason,
  isManager: boolean,
): boolean {
  if (reason === 'new_lead' || reason === 'appointment_confirmation') return true;
  return reason === 'routing_exception' && isManager;
}

/** Due tiers the strip renders as a counted tile. `all` is the clear-all and
 *  never has one. */
export function dueHasCountedTile(due: WorkspaceDueFilter): boolean {
  return due === 'breach' || due === 'due_now' || due === 'morning_priority';
}

/* ------------------------------------------------------------------------ *
 * COMMAND-CENTER-CRAFT-V1 -- THE OPENING LINE (S1 signature).
 *
 * THE BAR: a manager opens Team operations and knows in five seconds WHERE
 * WORK IS PILING UP and WHO NEEDS HELP -- without clicking a filter. The
 * screen already carries every figure needed to answer that; what it did not
 * carry was the SENTENCE. Seven honest tiles still leave the manager to do
 * the synthesis, and synthesis is the job the screen should be doing.
 *
 * DETERMINISTIC BY CONTRACT (the S1 law): every clause maps 1:1 to a figure
 * ALREADY RENDERED on this page -- the strip tiles and the workload rows.
 * Pure rules over already-served numbers. No new query, no read-model change,
 * no historical data, no ranking score, no adjective that is not a count.
 *
 * ORDER IS THE SEVERITY LADDER, and it is the same ladder the tiles already
 * tone by: breach-tone pressure first (overdue, then appointments at risk,
 * then unassigned exceptions), warm-tone next (due now, new leads). The line
 * names AT MOST the top pressure, the agent carrying the most overdue, and
 * the unowned pile -- three clauses, the S1 maximum.
 * ------------------------------------------------------------------------ */

/** The agent carrying the most overdue work, when the workload read is
 *  available and anyone is actually overdue. Ties break on the larger open
 *  count, then on label, so the sentence is stable between renders rather
 *  than flickering between equals. NEVER a "worst performer" claim -- it is
 *  the same overdueCount the workload table already shows. */
export function agentNeedingHelp(
  workload: readonly WorkspaceWorkloadRowView[] | null,
): WorkspaceWorkloadRowView | null {
  if (workload === null) return null;
  const overdue = workload.filter((row) => row.overdueCount > 0);
  if (overdue.length === 0) return null;
  return [...overdue].sort(
    (a, b) =>
      b.overdueCount - a.overdueCount ||
      b.openCount - a.openCount ||
      a.label.localeCompare(b.label),
  )[0] as WorkspaceWorkloadRowView;
}

export interface WorkspaceOpeningLine {
  readonly state: 'healthy' | 'attention' | 'quiet';
  readonly clauses: readonly string[];
}

/**
 * The composed opening line for the MANAGER lens.
 *
 * THE RULE, banked from the founder's review of #680: A SYNTHESIS LINE EARNS
 * ITS PLACE BY SAYING WHAT THE TILES CANNOT. V1 opened with "25 items are
 * past the response time ... 51 items have no owner" -- both of which are
 * tiles rendered directly beneath it. Deleting chips that duplicated tiles
 * and then writing prose that duplicated tiles is the same defect twice.
 *
 * So this line now carries ONLY cross-cutting facts -- relationships between
 * the strip and the workload table that no single tile can express -- and
 * when there are none it does not render at all.
 *
 * STATE: `attention` while any breach-tone figure is non-zero, `quiet`
 * otherwise. Zero clauses means no line, never a fabricated all-clear.
 */
export function composeManagerOpeningLine(
  view: Pick<CallcenterWorkspaceView, 'strip' | 'manager'>,
): WorkspaceOpeningLine {
  const clauses: string[] = [];

  // THE ONLY THING THE TILES CANNOT SAY: how the pile is DISTRIBUTED. Every
  // tile is a total; none of them can tell a manager that most of the overdue
  // sits with one person, because that is a relationship between the strip
  // and the workload table. That relationship is the whole job of this line.
  const stretched = agentNeedingHelp(view.manager?.workload ?? null);
  if (stretched !== null) {
    // SCOPE LABELS V1 (Lane 5, 2026-09-10; controller item 3b). The DELETED
    // clause here read " of the N", presenting the agent's overdueCount as a
    // SHARE of the strip's overdue tile. Traced to source, that relationship is
    // FALSE: getStaffLeadLoad counts the agent's whole org-wide assigned book
    // aged from leads.created_at against the escalation setting, while
    // computeWorkspaceStrip counts rows in THIS QUEUE at urgency 'breach' aged
    // from each item's own SLA anchor. Neither is a subset of the other, so
    // "9 of the 1" was arithmetic nonsense whenever the guard let it through
    // and a coincidence whenever it did not. The line now NAMES the scope it
    // is actually reporting instead of relating two incomparable figures.
    clauses.push(
      `${stretched.label} is carrying ${stretched.overdueCount} overdue in their total load, on ${stretched.openCount} open.`,
    );
  }

  // The SECOND cross-cutting fact: an agent who is paused but still holds
  // work. No tile counts it -- it is a status crossed with a workload -- and
  // it is the other way a queue silently stops draining.
  const stalled = (view.manager?.workload ?? []).filter(
    (row) => row.paused && row.openCount > 0,
  );
  if (stalled.length > 0) {
    const who =
      stalled.length === 1
        ? `${stalled[0]!.label} is paused and still holds ${stalled[0]!.openCount}`
        : `${stalled.length} paused agents still hold work`;
    clauses.push(`${who}.`);
  }

  // NO CLAUSE, NO LINE. If nothing cross-cutting is true, the line does not
  // render at all -- it does NOT fall back to restating a tile. That was the
  // V1 defect the founder caught: "25 items are past the response time" and
  // "51 items have no owner" were both tiles directly below.
  if (clauses.length === 0) return { state: 'quiet', clauses: [] };

  const breachPressure =
    view.strip.overdue > 0 ||
    view.strip.appointmentsAtRisk > 0 ||
    view.strip.exceptions > 0;
  return { state: breachPressure ? 'attention' : 'quiet', clauses };
}

/** True when the strip and the items agree (the same-rows law the page
 *  asserts in dev renders; the server computes both from one array). */
export function stripMatchesItems(view: CallcenterWorkspaceView): boolean {
  const items = view.items;
  return (
    view.strip.newLeads ===
      items.filter((i) => i.workReason === 'new_lead').length &&
    view.strip.appointmentsAtRisk ===
      items.filter((i) => i.workReason === 'appointment_confirmation').length &&
    view.strip.dueNow === items.filter((i) => i.urgency === 'due_now').length &&
    view.strip.exceptions ===
      items.filter((i) => i.workReason === 'routing_exception').length
  );
}

/* ------------------------------------------------------------------------ *
 * ADORACARE-VISUAL-SYSTEM-V1 -- the reference screen's PURE view logic
 * (My Work, agent lens, behind ?visual=v1; ui/visual-variant.ts).
 *
 * THE PRIMARY WORK OBJECT, in one sentence: the prioritised work queue plus
 * the selected lead's context. Every helper below serves a block that
 * justified itself against that sentence (run record s.7); nothing here
 * derives a new fact from the wire -- it re-presents the fields the server
 * already sends (ONE truth; the due-ness order stays the server's).
 * ------------------------------------------------------------------------ */

/**
 * THE WAITING FIGURE -- a tabular clock, honest to the wire. The server
 * sends `ageMinutes` (time since the SLA anchor), never a deadline, so the
 * figure is HOW LONG THIS HAS WAITED -- not "time over", which the wire
 * cannot support. Fixed-width parts (`19h 04m`, `2d 05h`) so a column of
 * them aligns and compares at a glance (TABULAR NUMBERS wherever values
 * are compared). Null -> '-' (never a fabricated zero).
 */
export function formatWaitingClock(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes)) return '-';
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m}m`;
  const hours = Math.floor(m / 60);
  if (hours < 24) return `${hours}h ${String(m % 60).padStart(2, '0')}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${String(hours % 24).padStart(2, '0')}h`;
}

export type WaitingStateTone = 'breach' | 'due' | 'info' | 'waiting';

export interface WaitingState {
  /** The .ac-state modifier (semantic family, never the brand accent). */
  readonly tone: WaitingStateTone;
  /** The WORD -- colour is never the sole carrier. */
  readonly word: string;
  /** The tabular figure, or '' when there is no clock to show. */
  readonly figure: string;
}

/** The STATE grammar for a queue row: word + figure from the row's own
 *  urgency token and age. The same truths dueStateLabel renders as a
 *  sentence, re-cut as fields (FIELDS, NOT SENTENCES). */
export function waitingState(item: CallcenterWorkspaceItemView): WaitingState {
  switch (item.urgency) {
    case 'breach':
      return { tone: 'breach', word: 'Overdue', figure: formatWaitingClock(item.ageMinutes) };
    case 'due_now':
      return { tone: 'due', word: 'Due now', figure: formatWaitingClock(item.ageMinutes) };
    case 'morning_priority':
      return { tone: 'info', word: 'Morning priority', figure: formatWaitingClock(item.ageMinutes) };
    case 'upcoming':
      // No "waited" duration exists for a not-yet-due callback (ageMinutes is
      // null by construction) -- the honest figure is empty, same as 'none'.
      return { tone: 'info', word: 'Upcoming', figure: '' };
    case 'none':
      return item.slaAnchorIso === null
        ? { tone: 'waiting', word: 'No due time', figure: '' }
        : { tone: 'waiting', word: 'Waiting', figure: formatWaitingClock(item.ageMinutes) };
    default: {
      const _exhaustive: never = item.urgency;
      void _exhaustive;
      return { tone: 'waiting', word: 'No due time', figure: '' };
    }
  }
}

/* ------------------------------------------------------------------------ *
 * THE STAT GRAMMAR, in two halves (CRAFT-CORRECTIVE, founder correction 1):
 * STATUS answers "what needs me now?" in one line on the ground; FILTERS
 * are quiet text toggles on the work plane. They never look alike.
 * ------------------------------------------------------------------------ */

/** One decision figure on the status line: a state (glyph + word) with its
 *  count and, where the tone earns one, the because-line. */
export interface NeedsYouFigure {
  readonly key: 'overdue' | 'due_now' | 'morning';
  readonly tone: WaitingStateTone;
  readonly count: number;
  /** The word beside the count ("overdue", "due now", "for the morning"). */
  readonly word: string;
  /** The because-line ("oldest 19h") or null. */
  readonly sub: string | null;
}

/**
 * THE STATUS LINE's figures -- only the tiers that carry a decision, only
 * when they are non-zero, in the server's own tier order (breach, due now,
 * morning priority). The counts are the strip's (the server's totals over
 * the whole set); the because-line is composeStripTileFacts's honest oldest
 * age (omitted when a feed is capped). Zero figures do not render: a line
 * that says "0 overdue" is teaching, not status.
 */
export function needsYouFigures(
  view: Pick<CallcenterWorkspaceView, 'items' | 'strip' | 'cappedSources' | 'workingHours'>,
): readonly NeedsYouFigure[] {
  const facts = composeStripTileFacts({ ...view, workingHours: { open: '', close: '', days: [] } });
  const out: NeedsYouFigure[] = [];
  if (view.strip.overdue > 0) {
    out.push({ key: 'overdue', tone: 'breach', count: view.strip.overdue, word: 'overdue', sub: facts.overdue });
  }
  if (view.strip.dueNow > 0) {
    out.push({ key: 'due_now', tone: 'due', count: view.strip.dueNow, word: 'due now', sub: null });
  }
  if (view.strip.morningPriority > 0) {
    out.push({ key: 'morning', tone: 'info', count: view.strip.morningPriority, word: `held to ${view.workingHours.open}`, sub: null });
  }
  return out;
}

/** One quiet text toggle in the filter row (the FILTER half). */
export interface QueueFilter {
  readonly key: string;
  readonly group: 'state' | 'reason';
  /** VERBATIM the strip's label where the strip counts it (ONE-NUMBER LAW:
   *  a count never sheds its scope-carrying label). */
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  /** The filter state this toggle writes when clicked (a toggle). */
  readonly next: CallCenterWorkspaceFilterState;
}

/**
 * The filter row's toggles, DECLARED ONCE as data. Group 1 (BY STATE) is the
 * clear-all plus the three due tiers; group 2 (BY REASON) is every reason
 * the lens can carry -- the two the strip counts (new leads, appointments
 * at risk) at the strip's figure, the rest counted over the loaded items
 * exactly as the surviving chips already were. Nothing the current strip
 * or chip row offered is unreachable: every filter is one click, with a
 * count on it. No tone: a filter is secondary by construction; the status
 * line carries the decision ink.
 */
export function queueFilters(
  view: Pick<CallcenterWorkspaceView, 'items' | 'strip' | 'availableReasons'>,
  filters: CallCenterWorkspaceFilterState,
): readonly QueueFilter[] {
  const isEverything = filters.reasons.length === 0 && filters.due === 'all';
  const onlyReason = (reason: WorkspaceWorkReason): boolean =>
    filters.reasons.length === 1 && filters.reasons[0] === reason;
  const dueToggle = (key: string, label: string, due: WorkspaceDueFilter, count: number): QueueFilter => ({
    key,
    group: 'state',
    label,
    count,
    active: filters.due === due,
    next: { ...filters, due: filters.due === due ? 'all' : due },
  });
  const state: QueueFilter[] = [
    {
      key: 'everything',
      group: 'state',
      label: 'All',
      count: view.items.length,
      active: isEverything,
      next: { reasons: [], due: 'all', agentStaffId: filters.agentStaffId },
    },
    dueToggle('overdue', 'Overdue (SLA breaches)', 'breach', view.strip.overdue),
    dueToggle('due-now', 'Due now', 'due_now', view.strip.dueNow),
    dueToggle('morning', 'Morning priority', 'morning_priority', view.strip.morningPriority),
  ];
  const reasonToggle = (reason: WorkspaceWorkReason, label: string, count: number): QueueFilter => ({
    key: `reason-${reason}`,
    group: 'reason',
    label,
    count,
    active: onlyReason(reason),
    next: { ...filters, reasons: onlyReason(reason) ? [] : [reason] },
  });
  const reason: QueueFilter[] = view.availableReasons.map((r) => {
    if (r === 'new_lead') return reasonToggle(r, 'New leads', view.strip.newLeads);
    if (r === 'appointment_confirmation') {
      return reasonToggle(r, 'Appointments at risk', view.strip.appointmentsAtRisk);
    }
    // CORRECTIVE-747 ROUND 2 (Gap 1a): the SAME fix as the page's chip row --
    // a set's label is never sampled from one arbitrary member via .find().
    // WORKSPACE_REASON_LABELS_FALLBACK is now the ONE label source, not
    // merely the empty-set fallback.
    const label = WORKSPACE_REASON_LABELS_FALLBACK[r];
    return reasonToggle(r, label, view.items.filter((i) => i.workReason === r).length);
  });
  return [...state, ...reason];
}

/** The ONE label source for every reason toggle (CORRECTIVE-747 ROUND 2, Gap
 *  1a) -- mirrors the page's WORKSPACE_REASON_FALLBACK_LABELS verbatim;
 *  change BOTH maps together (the name-order trap: FALLBACK_LABELS here vs
 *  LABELS_FALLBACK there -- same content, do not edit one and skip the
 *  other). 'callback' reads the neutral 'Callbacks', never due-specific
 *  wording, because the set it labels can mix due-now and promised
 *  (upcoming) rows. Kept here so the pure helper has no React import. */
export const WORKSPACE_REASON_LABELS_FALLBACK: Readonly<
  Record<WorkspaceWorkReason, string>
> = Object.freeze({
  new_lead: 'New lead',
  call_now: 'Call now',
  callback: 'Callbacks',
  appointment_confirmation: 'Confirm appointment',
  waiting_response: 'Waiting on response',
  no_show_recovery: 'Missed appointment',
  overdue_followup: 'Follow-up due',
  escalation: 'Needs attention',
  routing_exception: 'Unassigned - needs owner',
});

/** The masthead's quiet count line: what the queue holds, and whether a
 *  filter is narrowing it. */
export function queueSummaryLine(visible: number, total: number): string {
  if (total === 0) return 'Nothing in your queue';
  if (visible === total) return `${total} in your queue`;
  return `${visible} of ${total} in your queue (filtered)`;
}

/**
 * KEYBOARD OPERATION (Vercel/Geist bar): ArrowUp / ArrowDown / Home / End
 * move the selection through the queue; Enter (DataTable's own) selects.
 * Pure: returns the row index to move to, or null when the key is not a
 * navigation key or there is nowhere to go.
 */
export function nextQueueRowIndex(
  key: string,
  currentIndex: number,
  rowCount: number,
): number | null {
  if (rowCount <= 0 || currentIndex < 0) return null;
  const last = rowCount - 1;
  switch (key) {
    case 'ArrowDown':
      return currentIndex >= last ? null : currentIndex + 1;
    case 'ArrowUp':
      return currentIndex <= 0 ? null : currentIndex - 1;
    case 'Home':
      return currentIndex === 0 ? null : 0;
    case 'End':
      return currentIndex === last ? null : last;
    default:
      return null;
  }
}

/**
 * QUEUE-HEADING-V1 (Lane 5, 2026-09-10). The honest size of the queue the
 * manager is looking at.
 *
 * WHY THIS EXISTS: the queue table carried a `label` that DataTable renders as
 * `aria-label` only, so the eye saw an unnamed, uncounted table sitting under
 * a row of filter chips -- while the workload table directly above it had a
 * heading, a sub-line and a name. Reproduced at 1440 and 834 before it was
 * changed (frames in this package's evidence directory).
 *
 * THE FILTERED CASE IS THE POINT. "3 items" on a queue that actually holds 40
 * is how a manager concludes the morning is quiet and stops looking. When a
 * filter is narrowing the list the line says so and names both numbers.
 */
export function queueCountLine(visible: number, total: number): string {
  const v = Math.max(0, Math.trunc(visible))
  const t = Math.max(0, Math.trunc(total))
  if (t === 0) return 'Nothing in this queue right now.'
  if (v === t) return `${t} ${t === 1 ? 'item' : 'items'}.`
  return `Showing ${v} of ${t} - filters are narrowing this queue.`
}

/* ------------------------------------------------------------------------ *
 * ERROR-RECOVERY-V2 (Lane 5, 2026-09-10) -- THE RECOVERY MUST MATCH THE
 * ERROR.
 *
 * WHAT V1 GOT WRONG, and it is worth stating plainly because it is the kind of
 * defect that reads as helpfulness: V1 printed ONE sentence -- "This is usually
 * temporary" -- and ONE "Try again" button under EVERY load failure. A 401 is
 * not temporary; a 403 is not temporary; an unprovisioned account is not
 * temporary; a 404 on a section still rolling out is not temporary. Under all
 * four, retrying does nothing and the sentence tells the operator to keep
 * pressing a button that cannot help, directly under a card that has just told
 * them the actual remedy (sign in / ask your administrator / check back).
 *
 * THE SHARED CARD IS NOT CHANGED. DashboardLoadError
 * (frontend/src/dashboard/DashboardLoadError.tsx) is used by the workboard, the
 * doctor surfaces and the patient record, and it is NOT Lane 5's to edit -- so
 * this classifier MIRRORS its branch conditions and stays out of its way. When
 * the card handles a case, Lane 5 adds NOTHING: no second sentence, no retry
 * button, no contradiction of the guidance the card already gives.
 * `tests/unit/../workspace-error-recovery.test.ts` pins the mirror against the
 * card's real source so the two cannot drift.
 *
 * THE VERBATIM BRANCH IS THE OTHER HALF. In its fallback the card renders the
 * message EXACTLY AS GIVEN -- so whatever the server or the transport put in
 * `PortalApiError.message` lands on an operator's screen unedited. A dropped
 * connection surfaces as "Failed to fetch"; a thrown DatabaseError surfaces as
 * "DatabaseError: staff_lead_load_failed"; a Postgres error surfaces as
 * "permission denied for table leads"; an HTML error page parsed as JSON
 * surfaces as "Unexpected token '<', \"<!doctype html>\" is not valid JSON".
 *
 * V2's FIRST ATTEMPT AT THIS FAILED OPEN, and the correction is the point of
 * this comment. It asked whether a message LOOKED technical -- a blacklist of
 * shapes (a bare snake_case token, `HTTP 500`, a few error-class names) -- and
 * treated anything containing spaces as readable. Every counterexample above
 * contains spaces, so every one of them sailed through unchanged. A blacklist
 * of exception shapes can always be walked around by the next exception, and
 * extending it one string at a time never establishes a boundary.
 *
 * SO THE BOUNDARY IS AN ALLOWLIST, and no caller-supplied text crosses it. In
 * the verbatim branch the card is handed one of this module's own controlled
 * sentences -- either copy explicitly mapped to a STABLE BACKEND CODE, or one
 * of two defaults chosen by a STRUCTURAL fact (did an HTTP status reach us at
 * all). The caller's message is never inspected and never rendered.
 *
 * WORDING IS LENS-NEUTRAL ON PURPOSE. The error state renders BEFORE the view
 * arrives, so the lens is unknown at that moment: an agent and a manager see
 * the same words. "the queue" is true for both; "your team's work" was true for
 * only one of them.
 * ------------------------------------------------------------------------ */

/** What the page should render around the shared load-error card. */
export interface WorkspaceErrorPresentation {
  /** True when DashboardLoadError renders its OWN status-specific guidance
   *  (404 / not-provisioned / 401 / 403 / auth text). Lane 5 adds nothing. */
  readonly handledByCard: boolean
  /** The message to hand the card. In the verbatim branch this is ALWAYS one
   *  of this module's controlled strings -- never the caller's. */
  readonly message: string
  /** True when the controlled copy replaced what the caller supplied. */
  readonly substituted: boolean
  /** Retry is offered ONLY where retrying could plausibly work. */
  readonly canRetry: boolean
  /** The recovery sentence, or null when the card's own guidance stands. */
  readonly recoveryHint: string | null
}

/**
 * THE ONLY STRINGS THIS PAGE MAY PUT ON THE GLASS in the card's verbatim
 * branch. Exported so tests assert against the same constants the page renders,
 * rather than against copies that could drift.
 */
export const WORKSPACE_LOAD_ERROR_COPY = Object.freeze({
  /** No HTTP status reached us: the request failed, or the reply was not
   *  readable as the API's JSON envelope. */
  unreachable:
    'We could not reach the server, or its reply could not be read. Check your connection, then try again.',
  /** The server answered with a failure status. */
  server: 'Something went wrong while loading the queue. Trying again usually works.',
})

/**
 * EXPLICITLY MAPPED, OPERATOR-FACING OUTCOMES, keyed on the backend's STABLE
 * MACHINE CODE (`AdoraCareError.code`, the response `error` field) and never on
 * message text. This is the allowlist half of the boundary: a code has to be
 * written down here, with copy written for an operator, before anything but the
 * defaults above can render.
 *
 * `INTERNAL_ERROR` is `sendError`'s catch-all (api/router.ts), whose own message
 * is the bare "Something went wrong" -- mapped so the operator gets the sentence
 * that names what they were doing.
 *
 * The workspace endpoint's DELIBERATE operator messages are not in this map on
 * purpose: it throws `AuthError` (401) and `ForbiddenError` (403), both of which
 * the shared card already answers with its own guidance, and both of which
 * `loadErrorHandledByCard` routes there before this map is consulted.
 */
const MAPPED_OPERATOR_COPY: ReadonlyMap<string, string> = new Map([
  ['INTERNAL_ERROR', WORKSPACE_LOAD_ERROR_COPY.server],
])

/** The one recovery sentence for a failure a retry could fix. Lens-neutral. */
const RETRYABLE_RECOVERY_HINT =
  'If it keeps happening, tell your administrator what you were doing.'

/**
 * Mirrors DashboardLoadError's own branch conditions. Kept in the SAME order
 * the card evaluates them (404 -> not-provisioned -> 401 -> 403 -> auth text),
 * because that order is what decides which card an operator actually sees.
 */
export function loadErrorHandledByCard(
  message: string,
  status?: number,
  code?: string,
): boolean {
  const lower = message.toLowerCase()
  if (status === 404) return true
  if (code === 'NO_PROFILE' || lower.includes('no staff profile')) return true
  if (status === 401) return true
  if (status === 403) return true
  return (
    lower.includes('no authorization') ||
    lower.includes('not authenticated') ||
    lower.includes('invalid token')
  )
}

export function presentWorkspaceLoadError(
  message: string,
  status?: number,
  code?: string,
): WorkspaceErrorPresentation {
  if (loadErrorHandledByCard(message, status, code)) {
    // THE CARD HAS ALREADY SAID THE RIGHT THING. The message is passed through
    // UNTOUCHED -- the card's own auth-text detection reads it, so rewording it
    // here would silently reclassify the card underneath us.
    return {
      handledByCard: true,
      message,
      substituted: false,
      canRetry: false,
      recoveryHint: null,
    }
  }

  if (code === 'WORKSPACE_INCOMPLETE') {
    const copy = 'The complete permitted queue is unavailable. No partial totals are shown.';
    return { handledByCard: false, message: copy, substituted: copy !== message,
      canRetry: false,
      recoveryHint: 'Ask your administrator to check queue completeness before loading it again.' };
  }

  // THE ALLOWLIST. Everything from here down renders CONTROLLED COPY. The
  // caller's `message` is used for exactly one thing -- reporting whether it was
  // replaced -- and never reaches the card.
  //
  // WHICH DEFAULT APPLIES IS A STRUCTURAL FACT, not a reading of the text.
  // Traced through the client (`parseJsonEnvelope`, frontend/src/lib/portal-api.ts)
  // rather than assumed:
  //
  //   NO STATUS      the request never produced a response at all -- `fetch`
  //                  itself rejected. Nothing was answered.
  //   STATUS < 400   the ONLY way a success status reaches an error path: the
  //                  server answered, but the body could not be parsed as the
  //                  API envelope, so the client threw with `res.status`
  //                  carried through. A gateway's HTML error page does this.
  //   STATUS >= 400  the server answered and reported a failure.
  //
  // The first two are the same thing to an operator -- no usable answer came
  // back -- and share the `unreachable` sentence, which says both. Only the
  // third is a server-reported problem. The message is not consulted.
  const mapped = code === undefined ? undefined : MAPPED_OPERATOR_COPY.get(code)
  const serverReportedFailure =
    status !== undefined && Number.isFinite(status) && status >= 400
  const copy =
    mapped ??
    (serverReportedFailure
      ? WORKSPACE_LOAD_ERROR_COPY.server
      : WORKSPACE_LOAD_ERROR_COPY.unreachable)

  return {
    handledByCard: false,
    message: copy,
    substituted: copy !== message,
    canRetry: true,
    recoveryHint: RETRYABLE_RECOVERY_HINT,
  }
}

/** Agent report options come only from the authorized workload read, including
 * paused agents with no open work. This is not a complete organization roster. */
export function reportAgentOptions(view: import('../api').CallcenterWorkspaceView): readonly { id: number; label: string }[] | null {
  if (view.lens !== 'manager' || view.manager?.workload == null) return null;
  const agents = new Map<number, string>();
  for (const row of view.manager.workload) agents.set(row.staffUserId, row.label);
  for (const row of view.manager.pausedWithNoOpenWork) if (!agents.has(row.staffUserId)) agents.set(row.staffUserId, row.label);
  return [...agents].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label) || a.id - b.id);
}

/** Reuse only the exact enquiry returned by a fresh, gated manager queue read.
 * Duplicate identity is unavailable, never first-match guessing. No stage token,
 * contact, patient, payment or appointment facts are inferred from display text.
 */
export function managerEnquiryPreview(
  view: CallcenterWorkspaceView,
  leadId: number,
): LeadQuickViewModel | null {
  if (view.lens !== 'manager' || !Number.isSafeInteger(leadId) || leadId <= 0) return null;
  const matches = view.items.filter(item => item.leadId === leadId);
  if (matches.length !== 1) return null;
  const item = matches[0];
  const model = buildQuickViewFromTileItem({
    leadId, safeLeadId: item.safeLeadId ?? String(leadId),
    displayName: item.displayName, clinicLabel: item.clinicLabel,
    ageMinutes: item.ageMinutes, stage: null,
    ownerLabel: item.owner.label, assigned: item.owner.staffId === null ? null : true,
  });
  return {
    ...model,
    stageLabel: item.stageLabel ?? 'Current status unavailable',
    sourceLabel: item.sourceKind === 'source' ? item.source ?? 'Source unavailable' : 'Source unavailable',
    nextAction: item.nextActionLabel || null,
    activity: item.lastActivity === null ? [] : [{
      label: item.lastActivity.label,
      whenLabel: item.lastActivity.atIso ? formatIstDateTime(item.lastActivity.atIso) + ' IST' : null,
    }],
  };
}

/** Manager business state comes from recorded work/stage, never urgency. */
export function managerBusinessStatus(item: CallcenterWorkspaceItemView): string {
  if (item.workReason === 'callback') return 'Callback';
  if (item.workReason === 'escalation' || item.workReason === 'routing_exception') return 'Needs review';
  return item.stageLabel === 'New' ? 'New lead' : item.stageLabel || 'Status unavailable';
}
